import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useOutletContext, useParams, useSearchParams } from "react-router";
import { neverLoaded, usePolling } from "../../lib/use-polling";
import { api, type SyncRun } from "../../lib/api";
import { PageHeader } from "../../components/primitives/PageHeader";
import { Tabs, type TabItem } from "../../components/primitives/Tabs";
import { Segmented } from "../../components/primitives/Segmented";
import { CopyButton } from "../../components/primitives/CopyButton";
import { Pulse } from "../../components/primitives/Pulse";
import { PageHelp } from "../../components/PageHelp";
import { NotFound } from "../NotFound";
import {
  envOptions,
  filterConnectionsForEnv,
  filterPipelinesForEnv,
  summaryLine,
  type ClientConnection,
  type ClientEnvironment,
  type ClientOrg,
  type ClientPipeline,
} from "./client-model";

// The client workspace (spec §3.3): one header, an environment selector kept in
// ?env=, route-aware tabs, and a shared context every tab reads.

const ATTENTION_LIMIT = 50;

export interface ClientContextValue {
  org: ClientOrg;
  /** The selected environment from ?env=, or null for All. */
  env: string | null;
  environments: ClientEnvironment[];
  /** Filtered by `env`. */
  connections: ClientConnection[];
  allConnections: ClientConnection[];
  /** Filtered by `env`. */
  pipelines: ClientPipeline[];
  allPipelines: ClientPipeline[];
  /** Failed runs for the client (within `env`); "50+" when the page was full. */
  attentionCount: number | string;
  /** Refresh connections right after an edit instead of waiting for the poll. */
  refetchInstances: () => Promise<void>;
  refetchOrganizations: () => Promise<void>;
}

/**
 * The client, its environments, and the connections and pipelines already
 * filtered to the selected one.
 *
 * Every tab reads this instead of fetching for itself, so the six tabs agree
 * with each other and with the header, and switching between them costs
 * nothing.
 */
export function useClientContext(): ClientContextValue {
  return useOutletContext<ClientContextValue>();
}

interface RawInstance {
  id: string;
  organizationId: string;
  name: string;
  connectorType: string;
  environment?: string | null;
}

/**
 * The client workspace (spec §3.3): one header, the environment selector kept
 * in `?env=`, route-aware tabs, and the shared context every tab reads.
 *
 * A layout route rather than six pages, which is what lets the environment and
 * the loaded data survive moving between tabs.
 */
export function ClientWorkspace() {
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const env = searchParams.get("env") || null;

  const orgsQ = usePolling(() => api.listOrganizations(), 60000);
  const instancesQ = usePolling(() => api.listConnectorInstances(), 30000);
  const pipelinesQ = usePolling(() => api.listPipelines(id), 30000);
  const environmentsQ = usePolling(() => api.listEnvironments({ organizationId: id }), 60000);
  const failedQ = usePolling(
    () => api.listSyncLog({ organizationId: id, status: "failed", limit: ATTENTION_LIMIT }),
    15000,
  );

  // After a connection edit we refetch immediately; the overlay is dropped as
  // soon as the next natural poll lands.
  const [instancesOverride, setInstancesOverride] = useState<RawInstance[] | null>(null);
  useEffect(() => { setInstancesOverride(null); }, [instancesQ.data]);

  // After a rename we refetch immediately; the overlay is dropped as soon as
  // the next natural poll lands.
  const [orgsOverride, setOrgsOverride] = useState<ClientOrg[] | null>(null);
  useEffect(() => { setOrgsOverride(null); }, [orgsQ.data]);

  const org = useMemo(
    () => (orgsOverride ?? orgsQ.data?.organizations ?? []).find((o: ClientOrg) => o.id === id) as ClientOrg | undefined,
    [orgsOverride, orgsQ.data, id],
  );

  const allConnections: ClientConnection[] = useMemo(() => {
    const raw: RawInstance[] = instancesOverride ?? instancesQ.data?.instances ?? [];
    return raw
      .filter((i) => i.organizationId === id)
      .map((i) => ({ id: i.id, name: i.name, connectorType: i.connectorType, environment: i.environment ?? null }));
  }, [instancesOverride, instancesQ.data, id]);

  const allPipelines: ClientPipeline[] = pipelinesQ.data?.pipelines ?? [];
  const environments: ClientEnvironment[] = environmentsQ.data?.environments ?? [];

  const connections = useMemo(() => filterConnectionsForEnv(allConnections, env), [allConnections, env]);
  const pipelines = useMemo(() => filterPipelinesForEnv(allPipelines, env), [allPipelines, env]);

  // Under an environment the count is filtered from one page of at most
  // ATTENTION_LIMIT failed runs, so a client with more failures across
  // environments can read low here; the API has no per-environment filter yet.
  const attentionCount: number | string = useMemo(() => {
    const entries: SyncRun[] = failedQ.data?.entries ?? [];
    if (!env) return failedQ.data?.nextCursor ? `${ATTENTION_LIMIT}+` : entries.length;
    const ids = new Set(pipelines.map((p) => p.id));
    return entries.filter((r) => ids.has(r.pipelineId)).length;
  }, [failedQ.data, env, pipelines]);

  /** Pull connections again right after an edit instead of waiting for the poll.
   *  A failure is swallowed: the next natural poll reconciles regardless. */
  async function refetchInstances() {
    try {
      const r = await api.listConnectorInstances();
      setInstancesOverride((r?.instances ?? []) as RawInstance[]);
    } catch {
      // The next poll reconciles regardless.
    }
  }

  /** Pull the client again right after a rename, for the same reason. */
  async function refetchOrganizations() {
    try {
      const r = await api.listOrganizations();
      setOrgsOverride((r?.organizations ?? []) as ClientOrg[]);
    } catch {
      // The next poll reconciles regardless.
    }
  }

  if (orgsQ.status === "loading" || instancesQ.status === "loading" || pipelinesQ.status === "loading") {
    return <Pulse />;
  }
  if (neverLoaded(orgsQ) || neverLoaded(instancesQ) || neverLoaded(pipelinesQ)) {
    return <p className="page-help">Couldn't load this client. Retrying.</p>;
  }
  if (!org) return <NotFound />;

  const search = env ? `?env=${encodeURIComponent(env)}` : "";
  const base = `/clients/${org.id}`;
  const tabs: TabItem[] = [
    { to: `${base}${search}`, label: "Overview", end: true },
    { to: `${base}/pipelines${search}`, label: "Pipelines", count: pipelines.length },
    { to: `${base}/connections${search}`, label: "Connections", count: connections.length },
    { to: `${base}/activity${search}`, label: "Activity" },
    { to: `${base}/attention${search}`, label: "Needs attention", count: attentionCount },
    { to: `${base}/settings${search}`, label: "Settings" },
  ];

  const options = envOptions(environments, env, "All");

  /** Write the chosen environment into `?env=`, replacing rather than pushing so
   *  Back leaves the workspace instead of walking the selector's history. */
  function onEnvChange(next: string) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("env", next);
    else params.delete("env");
    setSearchParams(params, { replace: true });
  }

  const context: ClientContextValue = {
    org,
    env,
    environments,
    connections,
    allConnections,
    pipelines,
    allPipelines,
    attentionCount,
    refetchInstances,
    refetchOrganizations,
  };

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            <Link to="/clients" className="page-header__crumb">CLIENTS</Link>
            <span aria-hidden="true"> / </span>
            <span>{org.name}</span>
          </>
        }
        title={org.name}
        meta={
          <span className="page-header__id">
            <span title={org.id}>{org.id.slice(0, 8)}</span>
            <CopyButton value={org.id} label="Copy id" />
          </span>
        }
        summary={summaryLine(pipelines, connections)}
        actions={
          options.length > 1 ? (
            <Segmented options={options} value={env ?? ""} onChange={onEnvChange} label="Environment" />
          ) : undefined
        }
      />
      <Tabs items={tabs} label="Client sections" />
      <PageHelp path="/clients/:id" />
      <Outlet context={context} />
    </div>
  );
}
